import { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import "./Login.scss";
import useLogin from "../../hooks/useLogin";
import {
  validateLoginForm,
  type LoginFormValues,
} from "./utils/loginValidation";
import CloseEye from "../../Assets/Img/CloseEye";
import OpenEye from "../../Assets/Img/OpenEye";
import Input from "Components/Common/Input/Input";
import Button from "Components/Common/Button/Button";
import RoleSelectModal from "Components/Common/RoleSelectModal/RoleSelectModal";

export default function Login() {
  const rememberedEmail = localStorage.getItem("rememberedEmail") ?? "";
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(Boolean(rememberedEmail));
  const [showRoleModal, setShowRoleModal] = useState(false);

  const { handleLogin, loading, error } = useLogin();
  const loadingStatusId = "login-status";

  const formik = useFormik<LoginFormValues>({
    initialValues: {
      email: rememberedEmail,
      password: "",
    },
    validate: validateLoginForm,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: () => {
      setShowRoleModal(true);
    },
  });

  const submitWithRole = async (role: string) => {
    const { email, password } = formik.values;
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", email.trim());
    } else {
      localStorage.removeItem("rememberedEmail");
    }
    await handleLogin(email.trim(), password, role);
    setShowRoleModal(false);
  };

  const isSubmitDisabled = loading;

  useEffect(() => {
    if (!rememberMe && formik.values.email === rememberedEmail) {
      localStorage.removeItem("rememberedEmail");
    }
  }, [rememberMe, formik.values.email, rememberedEmail]);

  const getFieldError = useCallback(
    (key: keyof LoginFormValues): string => formik.errors[key] ?? "",
    [formik.errors],
  );

  return (
    <div className="loginWrapper">
      <div className="container">
        <div className="page">
          <section className="hero">
            <div className="hero-copy">
              <h1>Welcome back to CSC Construction</h1>
              <p>
                Sign in to continue managing projects, tracking work, and
                staying on schedule while the site moves forward.
              </p>
            </div>
          </section>

          <section className="login-panel" aria-labelledby="login-heading">
            <h2 id="login-heading">Project Login</h2>
            <p>Enter your credentials to access your CSC dashboard.</p>

            <form
              onSubmit={formik.handleSubmit}
              noValidate
              aria-describedby={error ? "form-error" : undefined}
            >
              <p
                id={loadingStatusId}
                className="sr-only"
                role="status"
                aria-live="polite"
              >
                {loading ? "Signing in. Please wait." : "Ready to sign in."}
              </p>

              <div className="field">
                <label htmlFor="login-email">Email address</label>
                <Input
                  id="login-email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  autoComplete="email"
                  disabled={loading}
                  placeholder="email@company.com"
                  aria-invalid={
                    formik.touched.email && Boolean(formik.errors.email)
                  }
                  aria-describedby={
                    formik.errors.email ? "email-error" : undefined
                  }
                  className={formik.errors.email ? "input-error" : ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email && (
                  <span id="email-error" className="field-error" role="alert">
                    {formik.errors.email}
                  </span>
                )}
              </div>

              <div className="field">
                <label htmlFor="login-password">Password</label>
                <div className="pw-wrap">
                  <Input
                    id="login-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.password}
                    autoComplete="current-password"
                    disabled={loading}
                    placeholder="********"
                    aria-invalid={Boolean(getFieldError("password"))}
                    aria-describedby={
                      getFieldError("password") ? "password-error" : undefined
                    }
                    className={getFieldError("password") ? "input-error" : ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <Button
                    type="button"
                    className="pw-toggle"
                    disabled={loading}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    aria-controls="login-password"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <OpenEye /> : <CloseEye />}
                  </Button>
                </div>
                {getFieldError("password") && (
                  <span
                    id="password-error"
                    className="field-error"
                    role="alert"
                  >
                    {formik.errors.password}
                  </span>
                )}
              </div>

              <div className="form-footer">
                <a href="/forgot-password" className="forgot-link">
                  Forgot password?
                </a>
              </div>

              {error && (
                <div
                  id="form-error"
                  className="form-error"
                  role="alert"
                  aria-live="polite"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="login-btn"
                disabled={isSubmitDisabled}
                aria-busy={loading}
                aria-describedby={loading ? loadingStatusId : undefined}
              >
                {loading ? (
                  <>
                    <span className="spinner" aria-hidden="true" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </form>
          </section>
        </div>
      </div>

      <RoleSelectModal
        show={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        onConfirm={submitWithRole}
        loading={loading}
        confirmLabel="Sign in"
      />
    </div>
  );
}
